/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Keyword} from "../../../../directives/content-editable.directive";
import {Dialog} from "../../../../models/dialog";
import {DialogButton, Type} from "../../../../models/dialog-button";
import {Event} from "../../../../models/event";
import {KeywordService} from "../../../../services/keyword.service";
import {Component, OnInit, Input, EventEmitter, Output} from "@angular/core";

@Component({
  selector: 'app-dialog-display',
  templateUrl: './dialog-display.component.html'
})
export class DialogDisplayComponent implements OnInit {
  
  mButtonTypes = Type;

  @Input()
  set dialog(dialog: Dialog) {
    this.mDialog = dialog;
    this.reset();
  }

  @Input()
  mUniqueIndex: number;

  @Input()
  set selectedButton(button: DialogButton) {
    this.mSelectedButton = button; 
  }

  @Input()
  mEvent: Event;
    
  @Output()
  selectButton = new EventEmitter<DialogButton>();

  //Dialog which is being edited/displayed...
  mDialog: Dialog;

  //Timestamp shown in the Dialog Header
  mTestTimestamp = new Date();

  //The selected Dialog Button
  mSelectedButton: DialogButton = null;
  
  //Map holding the Button Id to whether the mouse is over it or not
  mButtonIdToMouseOverMap: {[propKey: number]: boolean} = {};


  constructor(
  ) {}


  ngOnInit(): void {
    this.reset();
  }

  reset(): void {
    this.mTestTimestamp = new Date();
    this.mSelectedButton = null;
    this.mButtonIdToMouseOverMap = {};
  }


  getClassForButton(button: DialogButton): string[] {
    const retVal = ['btn'];
    if (button != null && button.color != null && button.color.red != null) {
      switch (button.color.red) {
        case 2: {
          retVal.push('btn-default');
          break;
        }
        case 3: {
          retVal.push('btn-primary');
          break;
        }
        case 4: {
          retVal.push('btn-success');
          break;
        }
        case 5: {
          retVal.push('btn-info');
          break;
        }
        case 6: {
          retVal.push('btn-warning');
          break;
        }
        case 7: {
          retVal.push('btn-danger');
          break;
        }
      }
    }
    return retVal;
  }

  buttonClicked(button: DialogButton): void {
    this.selectButton.emit(button);
  }
  
  isWaitButton(button: DialogButton): boolean {
    return button != null && button.type != null && (button.type == Type.ACCEPT_WAIT || button.type == Type.DECLINE_WAIT || button.type == Type.NONE_WAIT);
  }
  
  isOwnerButton(button: DialogButton): boolean {
    return button != null && button.type != null && (button.type == Type.ACCEPT || button.type == Type.ACCEPT_WAIT);
  }
  
  isCloseButton(button: DialogButton): boolean {
    return button != null && button.type != null && (button.type == Type.CLOSE);
  }
  
  isDeclineButton(button: DialogButton): boolean {
    return button != null && button.type != null && (button.type == Type.DECLINE || button.type == Type.DECLINE_WAIT);
  }
  
  onMouseEnter(button: DialogButton): void {
    this.mButtonIdToMouseOverMap[button.id] = true;
  }
  
  onMouseLeave(button: DialogButton): void {
    this.mButtonIdToMouseOverMap[button.id] = false;
  }
  
  isMouseOver(button: DialogButton): boolean {
    const val = this.mButtonIdToMouseOverMap[button.id];
    return val == null? false : val;
  }
  
  removeButtonClicked(button: DialogButton): void {
    //Remove the button from the Dialog's list of buttons, then reorder the list
    if (this.mDialog != null && this.mDialog.buttons != null) {
      const index = this.mDialog.buttons.indexOf(button);
      if (index > -1) {
        this.mDialog.buttons.splice(index, 1);
        this.reorderButtons();
      }
    }
  }
  
  moveButtonRightClicked(button: DialogButton): void {
    if (this.mDialog != null && this.mDialog.buttons != null) {
      const index = this.mDialog.buttons.indexOf(button);
      if (index > 0) {
        const otherButton = this.mDialog.buttons[index - 1];
        this.mDialog.buttons[index] = otherButton;
        this.mDialog.buttons[index - 1] = button;
        this.reorderButtons();
      }
    }
  }
  
  moveButtonLeftClicked(button: DialogButton): void {
    if (this.mDialog != null && this.mDialog.buttons != null) {
      const index = this.mDialog.buttons.indexOf(button);
      if (index > -1 && index < this.mDialog.buttons.length - 1) {
        const otherButton = this.mDialog.buttons[index + 1];
        this.mDialog.buttons[index] = otherButton;
        this.mDialog.buttons[index + 1] = button;
        this.reorderButtons();
      }
    }
  }

  private reorderButtons(): void {
    if (this.mDialog.buttons != null) {
      let index = 1;
      for (const button of this.mDialog.buttons) {
        button.order = index;
        button.code = index.toString(10);
        index = index + 1;
      }
    }
  }

}
